import { commonUtils } from '@sato/components';
import { useQuery } from '@tanstack/react-query';

import { contractApi } from '@api/contracts.api';
import {
  ContractStatusCode,
  ParkingSpaceContract,
} from '@models/contract.types';
import { apartmentStore } from '@stores/apartmentStore';
import { contractStore } from '@stores/contractStore';

import { useObservable } from './useObservable';

export const SEPARATELY_INVOICED_PARKING_SPACES_QUERY_KEY = [
  'separately-invoiced-parking-spaces',
];

export const fetchSeparatelyInvoicedParkingSpaces = async () =>
  (await contractApi.fetchSeparatelyInvoicedParkingSpaces()).data;

export type ParkingSpaceContractItem = ParkingSpaceContract & {
  /** Type of the invoicing.
   *
   * - `includedInRent`: The contract holder does not pay anything extra, since the parking space is included in rent.
   * - `invoicedInContract`: The parking space invoice is part of the apartment contract.
   * - `invoicedSeparately`: The contract holder pays the parking space with a separate invoice.
   */
  invoiceType: 'includedInRent' | 'invoicedInContract' | 'invoicedSeparately';
};

type UseParkingSpaceReturnType = {
  /** Parking space that is included in the price of the rental contract. */
  includedInRent: ParkingSpaceContractItem | undefined;
  /** Parking spaces that are part of the apartment contract but cost extra. */
  invoicedInContract: ParkingSpaceContractItem[];
  /** Parking spaces where the invoice is provided separate to the apartment contract. */
  invoicedSeparately: ParkingSpaceContractItem[];
  /** All of the users leased parking spaces */
  all: ParkingSpaceContractItem[];
  isLoading: boolean;
};

/**
 * Gets parking space contracts that are leased by the user in the selected apartment contract, and also separately leased parking space contracts.
 *
 * NOTE: This data model is different than the one in useParkingContract. This uses a model that is a combination of a parking product and parking
 * contract.
 *
 * TODO: We should have only one data model for parking contracts.
 */
export const useParkingSpaceContracts = (): UseParkingSpaceReturnType => {
  const {
    selectedApartment,
    parkingSpacesOnSelectedContract,
    isApartmentStoreFetching,
    isContractStoreFetching,
  } = useObservable(() => ({
    parkingSpacesOnSelectedContract:
      contractStore.selectedContract?.parkingSpaces,
    selectedApartment: apartmentStore.selectedApartment?.apartment,
    isApartmentStoreFetching: apartmentStore.isFetching,
    isContractStoreFetching: contractStore.isFetching,
  }));

  const { data: separatelyInvoicedParkingSpaces, isLoading } = useQuery(
    SEPARATELY_INVOICED_PARKING_SPACES_QUERY_KEY,
    fetchSeparatelyInvoicedParkingSpaces
  );

  const includedInRent =
    selectedApartment?.hasParkingSpace && selectedApartment.parkingSpaceId
      ? ({
          parkingSpaceNumber: selectedApartment.parkingSpaceId,
          invoiceType: 'includedInRent',
          parkingSpacetype: null,
          contractStartingDate: null,
          grossTotal: 0,
          isQuotaPlace: false,
          realEstateId: selectedApartment.realEstateId,
          parkingContractId: '',
          parkingSpaceId: '',
          isQuickRent: false,
          parkingContractType: 'INDEFINITELY_TYPE',
          contractEndDate: null,
          contractStatus: ContractStatusCode.ACTIVE_STATUS,
          hasTerminationInformationText: false,
          rentingParty: 'SATO_CUSTOMER_SERVICE',
          // TODO: `includedInRent` lacks data. Should be separated from `invoicedInContract` and `invoicedSeparately` so that we
          // don't need this error-prone "as" type cast.
        } as ParkingSpaceContractItem)
      : undefined;

  const invoicedInContract =
    parkingSpacesOnSelectedContract?.map<ParkingSpaceContractItem>(
      (parkingSpace) => ({
        ...parkingSpace,
        invoiceType: 'invoicedInContract',
      })
    ) ?? [];

  const invoicedSeparately =
    separatelyInvoicedParkingSpaces?.map<ParkingSpaceContractItem>(
      (parkingSpace) => ({
        ...parkingSpace,
        invoiceType: 'invoicedSeparately',
      })
    ) ?? [];

  const all = [
    includedInRent,
    ...invoicedInContract,
    ...invoicedSeparately,
  ].filter(commonUtils.isTruthy);

  return {
    includedInRent,
    invoicedInContract,
    invoicedSeparately,
    all,
    isLoading: isLoading || isApartmentStoreFetching || isContractStoreFetching,
  };
};
